/**
 * useHomeGroups Hook
 *
 * Fetches list of home groups using URQL.
 */

import { useQuery } from "urql";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const HomeGroupsDocument = `
  query HomeGroups($filter: HomeGroupFilter) {
    homeGroups(filter: $filter) {
      id
      name
      description
      location
      isOpen
      isActive
      memberCount
      imageUrl
      mapsUrl
      leader {
        id
        firstName
        lastName
      }
      schedules {
        id
        dayOfWeek
        startTime
        endTime
        isActive
      }
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface HomeGroupsFilter {
  isOpen?: boolean;
}

interface HomeGroup {
  id: string;
  name: string;
  description: string | null;
  location: string | null;
  isOpen: boolean;
  isActive: boolean;
  memberCount: number;
  imageUrl: string | null;
  mapsUrl: string | null;
  leader: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  };
  schedules: Array<{
    id: string;
    dayOfWeek: string;
    startTime: string;
    endTime: string | null;
    isActive: boolean;
  }>;
}

interface UseHomeGroupsOptions {
  filter?: HomeGroupsFilter;
  pause?: boolean;
}

interface UseHomeGroupsResult {
  homeGroups: HomeGroup[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Fetch list of home groups with optional filters.
 */
export function useHomeGroups(
  options: UseHomeGroupsOptions = {}
): UseHomeGroupsResult {
  const { filter, pause = false } = options;

  const [result, reexecuteQuery] = useQuery<{ homeGroups: HomeGroup[] }>({
    query: HomeGroupsDocument,
    variables: { filter },
    pause,
  });

  return {
    homeGroups: result.data?.homeGroups ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}
